import React, { useEffect } from 'react'; 
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';

import { initAnalytics } from './firebase'; 

import { AuthProvider, useAuth } from './context/AuthContext';
import { UserDataProvider } from "./context/UserDataContext";
import { getUserConsentStatus } from "./data/accountRepo";
import { initRevenueCat } from "./billing/RevenueCat";

import Home from './pages_auth/Home'
import ProfilePage from "./pages_auth/ProfilePage";
import HomePage from './pages_unauth/HomePage';
import SignupPage from './pages_unauth/SignupPage';
import SigninPage from './pages_unauth/SigninPage';
import StoryPage from "./pages_auth/StoryPage";
import VerifyEmailPage from "./pages_unauth/VerifyEmailPage";
import MyLibrary from "./pages_auth/MyLibrary"; 
import FlashcardsPage from './pages_auth/FlashcardsPage';
import PrivacyPolicy from './pages_unauth/PrivacyPolicy'; 
import CookiePolicy from './pages_unauth/CookiePolicy';
import TermsAndConditions from './pages_unauth/TermsAndConditions';
import Imprint from './pages_unauth/Imprint';
import ResetPasswordPage from "./pages_unauth/ResetPasswordPage";
import CompleteSignupPage from "./pages_unauth/CompleteSignupPage";
import LoadingScreen from "./components/modals/LoadingScreen";
import AnalyticsConsentGate from "./components/modals/AnalyticsConsentGate";

import { StatusBar, Style } from "@capacitor/status-bar";
import { SystemBars, SystemBarsStyle, SystemBarType } from "@capacitor/core";
import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";

function isPasswordProvider(user) {
  return (user?.providerData || []).some((p) => p?.providerId === "password");
}

function useConsentGate(user) {
  const uid = user?.uid ?? null;
  const needsConsentCheck = !!user && !isPasswordProvider(user) && !Capacitor.isNativePlatform();
  console.log("[ConsentGate] isNative:", Capacitor.isNativePlatform(), "needsConsentCheck:", needsConsentCheck, "uid:", user?.uid);

  const [acceptedTerms, setAcceptedTerms] = React.useState(true);
  const [checkedUid, setCheckedUid] = React.useState(null); // ✅ state, not ref
  const [fetching, setFetching] = React.useState(false);

  const consentLoading =
    needsConsentCheck && uid
      ? (checkedUid !== uid || fetching) // ✅ if not checked for this uid, we're loading
      : false;

  React.useEffect(() => {
    let alive = true;

    async function run() {
      if (!needsConsentCheck) {
        setAcceptedTerms(true);
        setCheckedUid(uid);
        setFetching(false);
        return;
      }

      if (!uid) {
        setAcceptedTerms(false);
        setCheckedUid(null);
        setFetching(false);
        return;
      }

      setFetching(true);
      try {
        const res = await getUserConsentStatus(uid);
        if (!alive) return;

        setAcceptedTerms(!!res.acceptedTerms);
        setCheckedUid(uid);           // ✅ triggers re-render
      } catch (e) {
        if (!alive) return;
        console.error("[ConsentGate] read failed", e?.code, e?.message);
        setAcceptedTerms(false);
        setCheckedUid(uid);           // ✅ still mark as checked to avoid infinite loading
      } finally {
        if (!alive) return;
        setFetching(false);
      }
    }

    run();
    return () => { alive = false; };
  }, [uid, needsConsentCheck]);

  return { consentLoading, acceptedTerms };
}



function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const { consentLoading, acceptedTerms } = useConsentGate(user);
  const location = useLocation();
  const isCompleteSignup = location.pathname === "/complete-signup";

  if (loading || consentLoading) return  <LoadingScreen debugLabel="RequireAuth" />;
  if (!user) return <Navigate to="/signin" replace />;

  // Email/password: must verify email
  if (isPasswordProvider(user) && !user.emailVerified) {
    return <Navigate to="/verify-email" replace />;
  }

  // OAuth: must have accepted terms
  // ✅ do NOT redirect if we are already on /complete-signup
  if (!isPasswordProvider(user) && !acceptedTerms && !isCompleteSignup) {
    return <Navigate to="/complete-signup" replace />;
  }

  return children;
}


function IndexRoute() {
  const { user, loading } = useAuth();
  const { consentLoading, acceptedTerms } = useConsentGate(user);

  const isNative = Capacitor.isNativePlatform();

  if (loading || consentLoading) return <LoadingScreen debugLabel="IndexRoute" />;

  if (user) {
    if (isPasswordProvider(user) && !user.emailVerified) {
      return <Navigate to="/verify-email" replace />;
    }

    if (!isPasswordProvider(user) && !acceptedTerms) {
      return <Navigate to="/complete-signup" replace />;
    }

    return <Navigate to="/home" replace />;
  }

  if (isNative) 
    return <Navigate to="/signup" replace />; // or "/signin"

  return <HomePage />;
}


function UnauthOnly({ children }) {
  const { user, loading } = useAuth();
  const { consentLoading, acceptedTerms } = useConsentGate(user);
  const location = useLocation();
  const isCompleteSignup = location.pathname === "/complete-signup";

  if (loading || consentLoading) return <LoadingScreen debugLabel="UnauthOnly" />;

  if (user) {
    if (isPasswordProvider(user) && !user.emailVerified) {
      return <Navigate to="/verify-email" replace />;
    }

    // OAuth user logged in but missing terms -> complete signup instead of /home
    // ✅ if missing terms, go to complete-signup UNLESS already there
    if (!isPasswordProvider(user) && !acceptedTerms && !isCompleteSignup) {
      return <Navigate to="/complete-signup" replace />;
    }

    // ✅ if already on complete-signup, let it render (don’t send to /home)
    if (isCompleteSignup) return children;

    return <Navigate to="/home" replace />;
  }

  return children;
}

function RevenueCatBootstrapper() {
  const { user, loading } = useAuth();

  useEffect(() => {
    if (loading) return;
    if (!user?.uid) return;

    initRevenueCat(user.uid);
  }, [loading, user?.uid]);

  return null;
}



function App() {

  // useEffect(() => {
  //   if (Capacitor.isNativePlatform()) return;
  
  //   // prevent double-inject (React StrictMode/dev)
  //   if (document.getElementById("Cookiebot")) return;
  
  //   const s = document.createElement("script");
  //   s.id = "Cookiebot";
  //   s.src = "https://consent.cookiebot.com/uc.js";
  //   s.type = "text/javascript";
  //   s.setAttribute("data-cbid", "d6a22caf-0799-46d1-b062-138b968cb678");
  //   document.head.appendChild(s);
  // }, []);

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== "android") return;
  
    const sub = CapApp.addListener("backButton", ({ canGoBack }) => {
      if (canGoBack) window.history.back();
      else CapApp.exitApp();
    });
  
    return () => sub.remove();
  }, []);

    // 🔐 This effect wires Cookiebot consent to Firebase Analytics
    useEffect(() => {
      if (Capacitor.isNativePlatform()) return;

      const initAnalyticsIfConsented = () => {
        try {
          if (
            window.Cookiebot &&
            window.Cookiebot.consent &&
            window.Cookiebot.consent.statistics
          ) {
            initAnalytics();
          }
        } catch (e) {
          console.warn("Error checking Cookiebot consent:", e);
        }
      };

      // 1) Check immediately (user may have already given consent earlier)
      initAnalyticsIfConsented();
  
      // 2) Run again when Cookiebot loads / user accepts
      const onLoadHandler = () => initAnalyticsIfConsented();
      const onAcceptHandler = () => initAnalyticsIfConsented();
  
      window.addEventListener("CookiebotOnLoad", onLoadHandler);
      window.addEventListener("CookiebotOnAccept", onAcceptHandler);
  
      return () => {
        window.removeEventListener("CookiebotOnLoad", onLoadHandler);
        window.removeEventListener("CookiebotOnAccept", onAcceptHandler);
      };
    }, []);

    // temorary enable analytics for  localhost - have to delete later
    // useEffect(() => {
    //   initAnalytics();
    // }, []);

    // 📱 iOS Status Bar fix (NATIVE ONLY)
    // useEffect(() => {
    //   // Only run on native (not web)
    //   if (Capacitor.isNativePlatform()) {
    //     StatusBar.setOverlaysWebView({ overlay: true });
    //     StatusBar.setStyle({ style: Style.Dark }); // or Light
    //   }
    // }, []);
    useEffect(() => {
      if (Capacitor.getPlatform() !== "android") return;
    
      const setupAndroidStatusBar = async () => {
        try {
          // Status bar (top) icons white
          await SystemBars.setStyle({ style: SystemBarsStyle.Dark, bar: SystemBarType.StatusBar });

          // Navigation bar (bottom) icons white (3-button / gesture indicator where applicable)
          await SystemBars.setStyle({ style: SystemBarsStyle.Dark, bar: SystemBarType.NavigationBar });
    
          // allow your CSS safe-area / gradient to show
          //await StatusBar.setOverlaysWebView({ overlay: true });
    
          // optional: solid fallback color (CSS gradient still wins visually)
          //await StatusBar.setBackgroundColor({ color: "#18181E" });
        } catch (e) {
          console.warn("Android status bar setup failed", e);
        }
      };
    
      setupAndroidStatusBar();
    }, []);
    

  return (
    <AuthProvider>
      <UserDataProvider>
        <Router>
          <RevenueCatBootstrapper />  
          <div className="App">
          <div className="status-bar-bg" />
            <Routes>
              <Route path="/" element={<IndexRoute />} />
              <Route path="/signup" element={
                <UnauthOnly>
                  <SignupPage />
                </UnauthOnly>
              } />

              <Route path="/signin" element={
                <UnauthOnly>
                  <SigninPage />
                </UnauthOnly>
              } />
              <Route path="/complete-signup" element={<CompleteSignupPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />
              <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
              <Route path="/imprint" element={<Imprint />} />
              <Route path="/home" element={
                <RequireAuth>
                  <Home />
                </RequireAuth>
              } />
              <Route path="/profile" element={
                  <RequireAuth>
                    <ProfilePage />
                  </RequireAuth>
                } />
              <Route path="/story/:id" element={
                  // <RequireAuth>
                    <StoryPage />
                  // </RequireAuth>
                } />
              <Route path="/library" element={
                  <RequireAuth>
                    <MyLibrary />
                  </RequireAuth>
                } />
              <Route path="/flashcards" element={
                <RequireAuth>
                  <FlashcardsPage />
                </RequireAuth>
                } />
              <Route path="/flashcards/review" element={
                <RequireAuth>
                  <FlashcardsPage />
                </RequireAuth>
                } />
            </Routes>

            <AnalyticsConsentGate />
          </div>
        </Router>
      </UserDataProvider>  
    </AuthProvider>
  );
}

export default App;
